import React from 'react';
import { FlagIcon, FlagIconCode } from 'react-flag-kit';
import { Control, UseFormResetField, useForm } from 'react-hook-form';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { intlHooks } from '@nxte-nl/intl-hooks';

import { DELAY_ERROR } from '../../constants/forms';
import { formHooks } from '../../hooks/formHooks/formHooks';
import { messageHooks } from '../../hooks/messageHooks/messageHooks';
import { storeSwitcherHooks } from '../../hooks/storeSwitcherHooks/storeSwitcherHooks';
import { StoreSwitcherMessages } from '../../types/Messages';
import { CountryCode, Currency, Language } from '../../types/apiEnums';
import { languageUtils } from '../../utils/languageUtils/languageUtils';
import { Button, ButtonFit, ButtonIntent, ButtonType } from '../Button/Button';
import {
  FORM_INPUT_CURRENCY,
  FormFieldsCurrency,
  FormInputCurrency,
  currencyRequiredValidationSchema,
} from '../FormInputCurrency/FormInputCurrency';
import {
  FORM_SELECT_COUNTRY,
  FormFieldsCountry,
  FormSelectCountry,
  selectCountryRequiredSchema,
} from '../FormSelectCountry/FormSelectCountry';
import {
  FORM_SELECT_LANGUAGE,
  FormFieldsLanguage,
  FormSelectLanguage,
  selectLanguageRequiredSchema,
} from '../FormSelectLanguage/FormSelectLanguage';
import { Link, LinkType, LinkIntent, LinkIconPosition } from '../Link/Link';
import { SelectItemType } from '../Select/Select';
import { Typography, TypographyColor } from '../Typography/Typography';
import styles from './LanguageForm.module.css';

const messages = defineMessages({
  title: {
    id: 'src.components.LanguageForm.title',
    defaultMessage: 'Select your country and language',
  },
  buttonCancel: {
    id: 'src.components.LanguageForm.buttonCancel',
    defaultMessage: 'Cancel',
  },
  buttonApply: {
    id: 'src.components.LanguageForm.buttonApply',
    defaultMessage: 'To apply',
  },
  formattedCurrency: {
    id: 'src.components.LanguageForm.formattedCurrency',
    defaultMessage: '{currencySymbol} ({currency})',
  },
});

export type LanguageCombinationPresentational = {
  currency: Currency;
  isDefault: boolean;
  languageCode: Language;
};

export type CountryCombinationPresentational = {
  country: CountryCode;
  languagesCombinations: LanguageCombinationPresentational[];
  url: string;
};

export const languageFormSchema = yup.object().shape({
  [FORM_SELECT_COUNTRY]: selectCountryRequiredSchema,
  [FORM_SELECT_LANGUAGE]: selectLanguageRequiredSchema,
  [FORM_INPUT_CURRENCY]: currencyRequiredValidationSchema,
});

export type Props = {
  countriesCombinations: CountryCombinationPresentational[] | null;
  initialCountry: CountryCode | null;
  language: string;
  onClose: () => void;
  onLanguageSelected: (formFields: FormFields) => void;
};

export type FormFields = FormFieldsCountry & FormFieldsLanguage & FormFieldsCurrency;

export const LANGUAGE_FORM = 'language-form';

export const hooks = {
  useHandleCountrySelectChange(
    data: CountryCombinationPresentational[] | null,
    setValue: (field: string, value: string) => void,
    resetField: UseFormResetField<FormFields>,
  ) {
    const dispayFormatLabel = storeSwitcherHooks.useFormatDisplayLabelCallback();
    const setSelectedCountry = messageHooks.useNext(StoreSwitcherMessages.SelectedCountry);
    const countryData = React.useMemo(() => {
      let countryData = null;
      if (data) {
        countryData = data.map((item: CountryCombinationPresentational) => {
          return {
            id: item.country,
            label: dispayFormatLabel(item.country) ?? item.country,
            icon: <FlagIcon code={item.country as FlagIconCode} size={16} style={{ height: 12 }} />,
          };
        });
      }
      return countryData;
    }, [data, dispayFormatLabel]);
    const handleCountrySelectChange = React.useCallback((id: string) => {
      const currentCountry = data?.find(item => item.country === id);
      if (currentCountry) {
        setSelectedCountry(currentCountry.country);
        resetField(FORM_SELECT_LANGUAGE, {
          defaultValue: currentCountry.languagesCombinations.find(el => el.isDefault)?.languageCode
            .toUpperCase() ?? currentCountry.languagesCombinations[0].languageCode.toUpperCase(),
        });
      }
      return setValue(FORM_SELECT_COUNTRY, id);
    }, [data, resetField, setSelectedCountry, setValue]);
    return { countryData, handleCountrySelectChange };
  },
  useHandleLanguageNCurrencyChange(
    data: CountryCombinationPresentational[] | null,
    selectedCountry: string | null,
    setValue: (field: string, value: string) => void,
  ) {
    const dispayFormatLabel = storeSwitcherHooks.useFormatDisplayLabelCallback();
    const languageDataNCurrency = React.useMemo(() => {
      let languageData: SelectItemType[] | null = null;
      if (data && selectedCountry && selectedCountry.length) {
        const countryDataTarget = data.find((item) => item.country === selectedCountry);
        if (countryDataTarget) {
          languageData = countryDataTarget.languagesCombinations.map(
            (langComb: LanguageCombinationPresentational) => {
              const languageType = langComb.languageCode.toUpperCase();
              let flagCode = langComb.languageCode.toUpperCase();
              // Temporary code until we won't decide how lang will be switched
              flagCode = flagCode === 'EN' ? 'GB' : flagCode;
              return {
                id: languageType,
                isDefault: langComb.isDefault,
                label: dispayFormatLabel(languageType, true) ?? '',
                icon: (
                  <FlagIcon
                    code={flagCode as FlagIconCode}
                    size={16}
                    style={{ height: 12 }}
                  />
                ),
              };
            },
          );
        }
      }
      return { languageData };
    }, [data, dispayFormatLabel, selectedCountry]);
    const handleLanguageSelectChange = React.useCallback((id: string) => {
      return setValue(FORM_SELECT_LANGUAGE, id);
    }, [setValue]);
    return {
      languageData: languageDataNCurrency.languageData,
      handleLanguageSelectChange,
    };
  },
  usePrefillData({
    language,
    country,
    currency,
  }: FormFields): FormFields {
    return React.useMemo(() => {
      return {
        language,
        country,
        currency,
      };
    }, [country, currency, language]);
  },
  useGetCurrency() {
    const intl = useIntl();
    return React.useCallback((currency: string, currencySymbol?: string) => {
      if (!currencySymbol) return currency;
      return intl.formatMessage(messages.formattedCurrency, {
        currencySymbol,
        currency,
      });
    },
    [intl]);
  },
  useGetDefaultLanguage(languageData: SelectItemType[] | null,
    language: string) {
    return React.useMemo(() => {
      if (languageData) {
        const foundElement = languageUtils.findLanguage({ languageData, language });

        if (foundElement) {
          return foundElement.id;
        }

        const defaultElement = languageUtils.findLanguage({
          languageData,
          language: languageUtils.getDefaultLanguage(),
        });

        if (defaultElement) {
          return defaultElement.id;
        }
      }
      return '';
    }, [language, languageData]);
  },
  useGetDefaultInitialCurrency(
    countriesCombinations: CountryCombinationPresentational[] | null,
    country: string | null,
  ) {
    const currency = React.useMemo(() => {
      const defaultLangObj = countriesCombinations
        ?.find((item) => item.country === country)
        ?.languagesCombinations.find((item) => item.isDefault);
      const firstLangObj = countriesCombinations
        ?.find((item) => item.country === country)?.languagesCombinations[0];
      const langObj = defaultLangObj ?? firstLangObj;
      return langObj?.currency;
    }, [countriesCombinations, country]);
    if (!currency) return '';
    const currencySymbol = intlHooks.useCurrencySymbol(String(currency));
    const getCurrency = hooks.useGetCurrency();
    return getCurrency(String(currency), currencySymbol);
  },
};

export const LanguageForm = React.memo((props: Props) => {
  const {
    onClose,
    onLanguageSelected,
    countriesCombinations,
    language: initialLanguage,
    initialCountry,
  } = props;

  const {
    formState: { isValid, isDirty, errors },
    watch,
    handleSubmit,
    control,
    getValues,
    setValue,
    register,
    resetField,
    reset,
  } = useForm<FormFields>({
    resolver: yupResolver(languageFormSchema),
    mode: 'all',
    delayError: DELAY_ERROR,
  });

  const {
    countryData,
    handleCountrySelectChange,
  } = hooks.useHandleCountrySelectChange(countriesCombinations,
    setValue, resetField);

  const country = watch(FORM_SELECT_COUNTRY) ?? initialCountry;
  const language = watch(FORM_SELECT_LANGUAGE) ?? initialLanguage;

  const {
    languageData,
    handleLanguageSelectChange,
  } = hooks.useHandleLanguageNCurrencyChange(
    countriesCombinations,
    country,
    setValue,
  );

  const defaultLanguage = hooks.useGetDefaultLanguage(languageData, language);

  const currency = hooks.useGetDefaultInitialCurrency(
    countriesCombinations, country,
  );

  const preFillData = hooks.usePrefillData({
    country,
    language: defaultLanguage,
    currency,
  });

  formHooks.usePreFillFields<FormFields>({
    isDirty,
    getValues,
    reset,
    preFillData,
  });

  const refCountry = React.useRef<HTMLDivElement>(null);
  const refLanguage = React.useRef<HTMLDivElement>(null);

  return !countryData || !languageData ? null : (
    <form
      className={styles.languageForm}
      onSubmit={handleSubmit(onLanguageSelected)}
    >
      <div className={styles.languageFormContent}>
        <div className={styles.inputWrapper}>
          <FormSelectCountry
            error={errors.country}
            name={FORM_SELECT_COUNTRY}
            control={(control as unknown) as Control<FormFieldsCountry>}
            items={countryData}
            onChange={handleCountrySelectChange}
            search={true}
            ref={refCountry}
          />
        </div>
        <div className={styles.inputWrapper}>
          <FormSelectLanguage
            error={errors.language}
            name={FORM_SELECT_LANGUAGE}
            control={(control as unknown) as Control<FormFieldsLanguage>}
            items={languageData}
            onChange={handleLanguageSelectChange}
            disabled={languageData.length === 1}
            search={true}
            ref={refLanguage}
          />
        </div>
        <div className={styles.inputWrapper}>
          <FormInputCurrency
            inputClassName={styles.inputField}
            isDisabled={true}
            value={currency}
            disabled
            {...register(FORM_INPUT_CURRENCY)}
          />
        </div>
      </div>

      <div className={styles.languageFormFooter}>
        <Link
          type={LinkType.Span}
          intent={LinkIntent.primary}
          iconPosition={LinkIconPosition.Left}
          onClick={onClose}
        >
          <Typography
            variant='bodySubText1'
            color={TypographyColor.Secondary}
          >
            <FormattedMessage {...messages.buttonCancel} />
          </Typography>
        </Link>
        <Button
          intent={ButtonIntent.primary}
          fit={ButtonFit.Inline}
          type={ButtonType.Submit}
          className={styles.languageFormApplyButton}
          disabled={!isValid}
        >
          <Typography color={TypographyColor.Tertiary} variant='body2'>
            <FormattedMessage {...messages.buttonApply} />
          </Typography>
        </Button>
      </div>
    </form>
  );
});

LanguageForm.displayName = 'LanguageForm';
